import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";

interface CartItem {
  id: string;
  variant_id: string;
  quantity: number;
  product_variants: {
    sku: string;
    price: number;
    size: string | null;
    color: string | null;
    products: {
      title: string;
      slug: string;
      product_images: Array<{ url: string }>;
    };
  };
}

interface CartContextType {
  cartItems: CartItem[];
  cartCount: number;
  isLoading: boolean;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addToCart: (variantId: string, quantity?: number) => Promise<void>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  removeFromCart: (cartItemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [hasMerged, setHasMerged] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [sessionId] = useState(() => {
    // Get or create session ID for guest users
    let id = localStorage.getItem("cart_session_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("cart_session_id", id);
    }
    return id;
  });

  // Merge guest cart into user cart when logging in
  useEffect(() => {
    if (!user || hasMerged) return;

    const mergeGuestCart = async () => {
      try {
        // Find guest cart by session_id
        const { data: guestCart } = await supabase
          .from("carts")
          .select("id")
          .eq("session_id", sessionId)
          .maybeSingle();

        if (!guestCart) {
          setHasMerged(true);
          return;
        }

        // Get guest cart items
        const { data: guestItems } = await supabase
          .from("cart_items")
          .select("variant_id, quantity")
          .eq("cart_id", guestCart.id);

        if (!guestItems?.length) {
          setHasMerged(true);
          return;
        }

        // Get or create user cart
        let userCartId: string;
        const { data: existingUserCart } = await supabase
          .from("carts")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (existingUserCart) {
          userCartId = existingUserCart.id;
        } else {
          const { data: newCart, error } = await supabase
            .from("carts")
            .insert({ user_id: user.id })
            .select("id")
            .single();
          if (error) throw error;
          userCartId = newCart.id;
        }

        // Get existing user cart items to check for duplicates
        const { data: existingUserItems } = await supabase
          .from("cart_items")
          .select("variant_id, quantity, id")
          .eq("cart_id", userCartId);

        const existingMap = new Map(
          existingUserItems?.map((item) => [item.variant_id, item]) || []
        );

        // Merge items
        for (const guestItem of guestItems) {
          const existing = existingMap.get(guestItem.variant_id);
          if (existing) {
            // Update quantity if item already exists
            await supabase
              .from("cart_items")
              .update({ quantity: existing.quantity + guestItem.quantity })
              .eq("id", existing.id);
          } else {
            // Insert new item
            await supabase.from("cart_items").insert({
              cart_id: userCartId,
              variant_id: guestItem.variant_id,
              quantity: guestItem.quantity,
            });
          }
        }

        // Clean up guest cart items and cart
        await supabase.from("cart_items").delete().eq("cart_id", guestCart.id);
        await supabase.from("carts").delete().eq("id", guestCart.id);

        // Invalidate queries to refresh cart data
        queryClient.invalidateQueries({ queryKey: ["cart"] });
        // Invalidate both the base key and the specific cart key to avoid stale UI
        queryClient.invalidateQueries({ queryKey: ["cart-items"] });
        queryClient.invalidateQueries({ queryKey: ["cart-items", userCartId] });
        queryClient.refetchQueries({ queryKey: ["cart-items", userCartId] });

        setHasMerged(true);
      } catch (error) {
        console.error("Failed to merge guest cart:", error);
        setHasMerged(true);
      }
    };

    mergeGuestCart();
  }, [user, sessionId, hasMerged, queryClient]);

  // Get or create cart
  const { data: cart } = useQuery({
    queryKey: ["cart", user?.id, sessionId],
    queryFn: async () => {
      // For logged in users, always use user_id cart
      if (user) {
        const { data: existingCart } = await supabase
          .from("carts")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle();

        if (existingCart) return existingCart;

        // Create new cart for user
        const { data: newCart, error } = await supabase
          .from("carts")
          .insert({ user_id: user.id })
          .select("id")
          .single();

        if (error) throw error;
        return newCart;
      }

      // For guest users, use session_id
      const { data: existingCart } = await supabase
        .from("carts")
        .select("id")
        .eq("session_id", sessionId)
        .maybeSingle();

      if (existingCart) return existingCart;

      // Create new cart for guest
      const { data: newCart, error } = await supabase
        .from("carts")
        .insert({ session_id: sessionId })
        .select("id")
        .single();

      if (error) throw error;
      return newCart;
    },
  });

  // Get cart items
  const { data: cartItems = [], isLoading } = useQuery({
    queryKey: ["cart-items", cart?.id],
    queryFn: async () => {
      if (!cart?.id) return [];

      const { data, error } = await supabase
        .from("cart_items")
        .select(`
          id,
          variant_id,
          quantity,
          product_variants (
            sku,
            price,
            size,
            color,
            products (
              title,
              slug,
              product_images (url)
            )
          )
        `)
        .eq("cart_id", cart.id);

      if (error) throw error;
      return data as CartItem[];
    },
    enabled: !!cart?.id,
    // Ensure the UI always reflects the latest cart state when navigating around
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const addToCartMutation = useMutation({
    mutationFn: async ({ variantId, quantity = 1 }: { variantId: string; quantity?: number }) => {
      if (!cart?.id) throw new Error("Cart not initialized");

      // Check if item already exists
      const { data: existingItem } = await supabase
        .from("cart_items")
        .select("id, quantity")
        .eq("cart_id", cart.id)
        .eq("variant_id", variantId)
        .maybeSingle();

      if (existingItem) {
        // Update quantity
        const { error } = await supabase
          .from("cart_items")
          .update({ quantity: existingItem.quantity + quantity })
          .eq("id", existingItem.id);

        if (error) throw error;
      } else {
        // Insert new item
        const { error } = await supabase
          .from("cart_items")
          .insert({
            cart_id: cart.id,
            variant_id: variantId,
            quantity,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      const cartId = cart?.id;

      // Invalidate broadly + refetch to guarantee the UI updates immediately
      queryClient.invalidateQueries({ queryKey: ["cart-items"] });
      if (cartId) {
        queryClient.invalidateQueries({ queryKey: ["cart-items", cartId] });
        queryClient.refetchQueries({ queryKey: ["cart-items", cartId] });
      }

      // Open mini-cart drawer
      setIsCartOpen(true);

      toast.success("Added to cart");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to add to cart");
    },
  });

  const updateQuantityMutation = useMutation({
    mutationFn: async ({ cartItemId, quantity }: { cartItemId: string; quantity: number }) => {
      if (quantity <= 0) {
        const { error } = await supabase
          .from("cart_items")
          .delete()
          .eq("id", cartItemId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("cart_items")
          .update({ quantity })
          .eq("id", cartItemId);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items", cart?.id] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to update quantity");
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: async (cartItemId: string) => {
      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("id", cartItemId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items", cart?.id] });
      toast.success("Removed from cart");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to remove item");
    },
  });

  const clearCartMutation = useMutation({
    mutationFn: async () => {
      if (!cart?.id) return;

      const { error } = await supabase
        .from("cart_items")
        .delete()
        .eq("cart_id", cart.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart-items", cart?.id] });
      toast.success("Cart cleared");
    },
  });

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const value: CartContextType = {
    cartItems,
    cartCount,
    isLoading,
    isCartOpen,
    openCart,
    closeCart,
    addToCart: (variantId: string, quantity?: number) =>
      addToCartMutation.mutateAsync({ variantId, quantity }),
    updateQuantity: (cartItemId: string, quantity: number) =>
      updateQuantityMutation.mutateAsync({ cartItemId, quantity }),
    removeFromCart: (cartItemId: string) =>
      removeFromCartMutation.mutateAsync(cartItemId),
    clearCart: () => clearCartMutation.mutateAsync(),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
